/**
 * Look up Bortle class from the light pollution tile server by sampling pixel
 * color. Uses the edge-cached tile endpoint (TILE_SERVER_URL), not raw PMTiles
 * range requests — the tile endpoint is more reliable because it's pre-
 * extracted and CDN-cached, whereas range requests against the PMTiles archive
 * failed silently for some zoom-8 tiles (ERI-721).
 *
 * Uses the same color-to-Bortle mapping as scripts/sample-bortle.ts
 * and components/dashboard/BortleLegend.tsx.
 */

import { TILE_SERVER_URL, BORTLE_SAMPLE_ZOOM } from './tileConfig';

/** Bound each tile fetch so a slow/unhealthy tile server can't hang callers (best-effort lookup). */
const TILE_FETCH_TIMEOUT_MS = 5000;

/** Bortle scale reference colors (RGB + class number) */
const BORTLE_COLORS: [number, number, number, number][] = [
  [0x00, 0x00, 0x00, 1], // Excellent dark sky
  [0x33, 0x33, 0x33, 2], // Typical truly dark site
  [0x00, 0x00, 0xcc, 3], // Rural sky
  [0x00, 0x70, 0x99, 4], // Rural/suburban transition
  [0x00, 0xcc, 0x00, 5], // Suburban sky
  [0x99, 0xcc, 0x00, 6], // Bright suburban sky
  [0xff, 0xff, 0x00, 7], // Suburban/urban transition
  [0xff, 0x88, 0x00, 8], // City sky
  [0xff, 0x00, 0x00, 9], // Inner-city sky
];

function colorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

/** Map an RGBA pixel to a Bortle class (1-9), or null if no data. */
export function colorToBortle(r: number, g: number, b: number, a: number): number | null {
  // Threshold at 32 (not 128): antialiased/edge pixels on tile borders carry
  // real color data with low alpha; previously those read as "no data" and
  // the badge silently vanished. Only truly empty pixels should fail out.
  if (a < 32) return null;
  if (r < 20 && g < 20 && b < 20) return 1; // Very dark pixel

  let bestBortle = 1;
  let bestDist = Infinity;
  for (const [cr, cg, cb, bortle] of BORTLE_COLORS) {
    const dist = colorDistance(r, g, b, cr, cg, cb);
    if (dist < bestDist) {
      bestDist = dist;
      bestBortle = bortle;
    }
  }
  return bestBortle;
}

/** Convert lat/lng to tile coordinates + pixel offset within the tile. */
export function latLngToTilePixel(lat: number, lng: number, zoom: number) {
  const n = 2 ** zoom;
  const xTile = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const yTile = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  );
  const xFrac = ((lng + 180) / 360) * n - xTile;
  const yFrac =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n - yTile;
  return {
    x: xTile,
    y: yTile,
    pixelX: Math.floor(xFrac * 256),
    pixelY: Math.floor(yFrac * 256),
  };
}

/**
 * Decoded-tile cache: many favorites fall inside the same zoom-8 tile, so
 * caching the decoded ImageData eliminates redundant network fetches *and*
 * redundant PNG decodes. Also shields against transient range-request
 * flakiness by serving a cached copy to later callers.
 */
type CachedTile = ImageData | null; // null = confirmed no-data tile
const tileCache = new Map<string, CachedTile>();
const inflightTiles = new Map<string, Promise<CachedTile>>();

const tileKey = (z: number, x: number, y: number) => `${z}/${x}/${y}`;

const tileUrl = (z: number, x: number, y: number) =>
  TILE_SERVER_URL.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));

/** Fetch + decode a single tile from the tile server, with one retry and in-memory caching. */
async function fetchTileImageData(z: number, x: number, y: number): Promise<CachedTile> {
  const key = tileKey(z, x, y);
  if (tileCache.has(key)) return tileCache.get(key)!;
  const existing = inflightTiles.get(key);
  if (existing) return existing;

  const url = tileUrl(z, x, y);

  const load = (async (): Promise<CachedTile> => {
    const tryOnce = async (): Promise<CachedTile> => {
      // Bound each fetch so a slow/unhealthy tile server can't hang the caller
      // (the tile lookup is best-effort — it degrades to a null Bortle class).
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TILE_FETCH_TIMEOUT_MS);
      let res: Response;
      try {
        res = await fetch(url, { signal: controller.signal });
      } finally {
        clearTimeout(timer);
      }
      if (!res.ok) {
        if (res.status === 404) return null; // no data for this tile
        throw new Error(`tile fetch ${res.status}`);
      }
      const blob = await res.blob();
      const bitmap = await createImageBitmap(blob);
      const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0);
      return ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    };

    try {
      const data = await tryOnce();
      tileCache.set(key, data);
      return data;
    } catch (err) {
      // Single retry after a short delay handles transient network /
      // createImageBitmap flakes without bloating latency for real failures.
      await new Promise((resolve) => setTimeout(resolve, 250));
      try {
        const data = await tryOnce();
        tileCache.set(key, data);
        return data;
      } catch (retryErr) {
        console.error('[lookupBortle] tile fetch failed', { z, x, y, url }, retryErr ?? err);
        // Don't cache failures — next call gets a fresh attempt.
        return null;
      }
    } finally {
      inflightTiles.delete(key);
    }
  })();

  inflightTiles.set(key, load);
  return load;
}

/**
 * Look up the Bortle class for a given latitude/longitude.
 *
 * Fetches the relevant tile from the PMTiles archive (cached in memory on
 * repeat calls), decodes it, and samples the pixel at the location's
 * position within the tile.
 *
 * Returns null if no data is available for that location.
 */
export async function lookupBortle(lat: number, lng: number): Promise<number | null> {
  try {
    const { x, y, pixelX, pixelY } = latLngToTilePixel(lat, lng, BORTLE_SAMPLE_ZOOM);
    const imageData = await fetchTileImageData(BORTLE_SAMPLE_ZOOM, x, y);
    if (!imageData) return null;

    // ImageData.data is a Uint8ClampedArray laid out as RGBA rows.
    const idx = (pixelY * imageData.width + pixelX) * 4;
    return colorToBortle(imageData.data[idx], imageData.data[idx + 1], imageData.data[idx + 2], imageData.data[idx + 3]);
  } catch (err) {
    console.error('[lookupBortle] failed for', lat, lng, err);
    return null;
  }
}

/**
 * Batch lookup Bortle classes for multiple locations.
 * Returns a map of location key -> Bortle class.
 *
 * Concurrent calls that share a tile collapse to a single fetch via the
 * module-level cache.
 */
export async function lookupBortleBatch(
  locations: { key: string; lat: number; lng: number }[],
): Promise<Record<string, number>> {
  const results: Record<string, number> = {};
  await Promise.allSettled(
    locations.map(async (loc) => {
      const bortle = await lookupBortle(loc.lat, loc.lng);
      if (bortle !== null) {
        results[loc.key] = bortle;
      }
    }),
  );
  return results;
}

/** Reset module-level caches. Exposed for tests. */
export function __resetBortleLookupCacheForTests(): void {
  tileCache.clear();
  inflightTiles.clear();
}
